// Read-only Budget page: budget items of the departments the user can access by ACL.
// Department comes from initData.myDepartments; rows from apiMyBudgetItems (the same
// annual/requested/returned/available numbers the request form's budget cascade shows,
// shared budgets included). Year chips + search filter client-side.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Search } from "lucide-react";

import { ApiError, apiFetch } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useInitData } from "@/lib/init-data";
import { formatAmount, type BudgetItem } from "@/lib/payreq";
import { matchesAllWords } from "@/lib/search";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Combobox } from "@/components/ui/combobox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";

function fetchMyBudgetItems(token: string, departmentId: number, signal?: AbortSignal) {
  return apiFetch<{ items?: BudgetItem[] }>("/api/myPaymentRequest/apiMyBudgetItems", {
    token,
    method: "POST",
    body: { departmentId },
    signal,
  });
}

/** "Dept : Head : Year" → the head part (dept is the picker, year is the badge). */
function headOf(name: string): string {
  const parts = name.split(" : ");
  return parts.length >= 3 ? parts.slice(1, -1).join(" : ") : name;
}

export default function BudgetPage() {
  const { token } = useAuth();
  const initData = useInitData();

  const [departmentId, setDepartmentId] = useState<number | null>(null);
  const [items, setItems] = useState<BudgetItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [year, setYear] = useState<string | null>(null); // null = all years
  const [search, setSearch] = useState("");

  // Preselect the first (or only) department once init data arrives.
  useEffect(() => {
    if (departmentId == null && initData.myDepartments.length > 0) {
      setDepartmentId(initData.myDepartments[0].id);
    }
  }, [departmentId, initData.myDepartments]);

  useEffect(() => {
    if (!token || departmentId == null) return;
    const ctrl = new AbortController();
    setLoading(true);
    fetchMyBudgetItems(token, departmentId, ctrl.signal)
      .then((d) => {
        setItems(d.items ?? []);
        setYear(null);
      })
      .catch((e) => {
        if (ctrl.signal.aborted) return;
        toast.error(e instanceof ApiError ? e.message : "Could not load budget items.");
        setItems([]);
      })
      .finally(() => {
        if (!ctrl.signal.aborted) setLoading(false);
      });
    return () => ctrl.abort();
  }, [token, departmentId]);

  const years = useMemo(() => {
    const ys = [...new Set((items ?? []).map((i) => i.year).filter((y): y is string => !!y))];
    return ys; // backend already orders newest year first
  }, [items]);

  const visible = useMemo(
    () =>
      (items ?? []).filter(
        (i) => (year == null || i.year === year) && matchesAllWords(search, i.name)
      ),
    [items, year, search]
  );

  const totals = useMemo(
    () =>
      visible.reduce(
        (a, i) => ({
          amount: a.amount + (i.amount ?? 0),
          available: a.available + (i.availableAmount ?? 0),
        }),
        { amount: 0, available: 0 }
      ),
    [visible]
  );

  return (
    <div className="flex flex-col gap-3 p-4">
      <div className="flex flex-col gap-1.5">
        <Label>Department</Label>
        <Combobox
          options={initData.myDepartments.map((d) => ({
            id: d.id,
            name: d.name ?? String(d.id),
          }))}
          value={departmentId}
          onValueChange={(id) => setDepartmentId(id)}
          placeholder={
            initData.myDepartments.length === 0 ? "No departments accessible" : "Select department…"
          }
          disabled={initData.myDepartments.length === 0}
        />
      </div>

      <div className="relative">
        <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search budget items…"
          className="pl-9"
          aria-label="Search budget items"
        />
      </div>

      {years.length > 1 && (
        <div className="no-scrollbar -mx-4 flex gap-2 overflow-x-auto px-4">
          {[null, ...years].map((y) => (
            <Button
              key={y ?? "all"}
              variant={year === y ? "default" : "outline"}
              size="sm"
              className="shrink-0 rounded-full"
              onClick={() => setYear(y)}
            >
              {y ?? "All years"}
            </Button>
          ))}
        </div>
      )}

      {loading || items == null ? (
        <div className="flex flex-col gap-2">
          {Array.from({ length: 5 }).map((_, i) => (
            <Card size="sm" key={i}>
              <CardContent className="flex flex-col gap-2">
                <Skeleton className="h-4 w-2/3" />
                <Skeleton className="h-2 w-full" />
                <Skeleton className="h-3 w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center gap-1 py-16 text-center">
          <p className="text-sm font-medium">No budget items</p>
          <p className="text-xs text-muted-foreground">
            {search || year
              ? "Try a different search or year."
              : "This department has no budget items you can view."}
          </p>
        </div>
      ) : (
        <>
          <Card size="sm">
            <CardContent className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">
                {visible.length} item{visible.length === 1 ? "" : "s"} · Annual{" "}
                {formatAmount(totals.amount)}
              </span>
              <span
                className={cn(
                  "font-semibold",
                  totals.available > 0 ? "text-green-700 dark:text-green-400" : "text-destructive"
                )}
              >
                Available {formatAmount(totals.available)}
              </span>
            </CardContent>
          </Card>

          <ul className="flex flex-col gap-2">
            {visible.map((b) => {
              const annual = b.amount ?? 0;
              const returned = b.returnred ?? 0;
              const requested = b.requested ?? 0;
              const capacity = annual + returned;
              const used = capacity > 0 ? Math.min(100, (requested / capacity) * 100) : 0;
              const available = b.availableAmount ?? 0;
              return (
                <li key={b.id}>
                  <Card size="sm">
                    <CardContent className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <span className="min-w-0 text-sm font-medium">{headOf(b.name)}</span>
                        <span className="flex shrink-0 items-center gap-1">
                          {b.year && (
                            <Badge variant="secondary" className="text-muted-foreground">
                              {b.year}
                            </Badge>
                          )}
                          {!b.paymentRequestEnabled && (
                            <Badge variant="outline" className="text-muted-foreground">
                              Requests off
                            </Badge>
                          )}
                        </span>
                      </div>
                      <Progress value={used} />
                      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                        <span>
                          Annual {formatAmount(annual)}
                          {returned > 0 ? ` · Returned ${formatAmount(returned)}` : ""}
                          {` · Requested ${formatAmount(requested)}`}
                        </span>
                        <span
                          className={cn(
                            "text-sm font-semibold",
                            available > 0
                              ? "text-green-700 dark:text-green-400"
                              : "text-destructive"
                          )}
                        >
                          {formatAmount(available)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
